import React, { createContext, useContext, useRef, useState, useCallback, useEffect, useMemo } from "react";
import { DEFAULT_KIT, generateKitBuffers, getKitById } from "./sampleKits";
import { exportBufferToWav } from "./audioBufferToWav";
import { playFMNote, playSampleNote, playSoundfontNote } from "./instrumentSynth";
import { loadInstrument, getCachedInstrument } from "./soundfontLoader";
import { useDAWSession } from "./DAWSessionProvider";

const DAWContext = createContext(null);

export function useDAW() {
  const ctx = useContext(DAWContext);
  if (!ctx) throw new Error("useDAW must be used within DAWProvider");
  return ctx;
}

const NUM_PADS = 16;
const STEPS_PER_BAR = 16;
const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD = 0.1;

function createEmptyPattern(numPads, totalSteps) {
  return Array.from({ length: numPads }, () => new Array(totalSteps).fill(false));
}

let audioTrackIdCounter = 0;
let instrumentTrackIdCounter = 0;
let noteIdCounter = 0;

function applyAutomation(gainNode, automation, startTime, bpm) {
  if (!automation || automation.length === 0) return;
  const beatDur = 60 / bpm;
  // Set initial value
  const firstVal = automation[0].value / 100;
  const firstTime = startTime + automation[0].beat * beatDur;
  gainNode.gain.setValueAtTime(firstVal, firstTime);
  // Ramp to each subsequent point
  for (let i = 1; i < automation.length; i++) {
    const time = startTime + automation[i].beat * beatDur;
    gainNode.gain.linearRampToValueAtTime(automation[i].value / 100, time);
  }
}

export default function DAWProvider({ children, sessionRole: sessionRoleProp }) {
  const audioCtxRef = useRef(null);
  const masterGainRef = useRef(null);
  const sampleBuffersRef = useRef([]);
  const originalBuffersRef = useRef([]);
  const schedulerRef = useRef(null);
  const nextStepTimeRef = useRef(0);
  const currentStepRef = useRef(0);
  const audioTrackSourcesRef = useRef([]);
  const instrumentSourcesRef = useRef([]);
  const playbackStartTimeRef = useRef(0);
  const animFrameRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [volume, setVolume] = useState(80);
  const [bpm, setBpm] = useState(120);
  const [swing, setSwing] = useState(0);
  const [metronomeOn, setMetronomeOn] = useState(false);
  const [kit, setKit] = useState(DEFAULT_KIT);
  const kitRef = useRef(kit);
  const [activePads, setActivePads] = useState({});

  // Step sequencer
  const [numBars, setNumBars] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [pattern, setPattern] = useState(() => createEmptyPattern(NUM_PADS, STEPS_PER_BAR));
  const [mutedRows, setMutedRows] = useState(() => new Array(NUM_PADS).fill(false));
  const [drumsMuted, setDrumsMuted] = useState(false);

  // Custom pad names and sounds (null = use default kit)
  const [customPadNames, setCustomPadNames] = useState(() => new Array(NUM_PADS).fill(null));
  const [customPadFlags, setCustomPadFlags] = useState(() => new Array(NUM_PADS).fill(false));

  // Audio tracks (vocals, instruments, etc.)
  const [audioTracks, setAudioTracks] = useState([]);
  // { id, name, buffer: AudioBuffer, muted, solo, volume, startTime, trimStart, trimEnd }

  // Instrument tracks (piano/MIDI)
  const [instrumentTracks, setInstrumentTracks] = useState([]);
  // { id, name, notes: [{ id, midi, start, duration, velocity }], muted, solo, volume, startTime, customSampleBuffer }

  // Drums clip offset (seconds)
  const [drumsStartTime, setDrumsStartTime] = useState(0);

  // MIDI recording — per-key tracking: { [midi]: [{ id, startBeat, trackId }] }
  const recordingNotesRef = useRef({});

  // Arrangement view
  const [selectedTrackId, setSelectedTrackId] = useState("drums");
  const [playheadPosition, setPlayheadPosition] = useState(0);

  // Arrangement length — auto-computed from content, minimum 8 bars
  const barDuration = (60 / bpm) * 4; // seconds per bar
  const arrangementBars = useMemo(() => {
    const MIN_BARS = 8;
    const beatDur = 60 / bpm;
    // Drums end time
    const drumsEndTime = drumsStartTime + numBars * barDuration;
    // Audio tracks end time
    let maxEnd = drumsEndTime;
    audioTracks.forEach((t) => {
      if (!t.buffer) return;
      const clipEnd = (t.startTime ?? 0) + ((t.trimEnd ?? 1) - (t.trimStart ?? 0)) * t.buffer.duration;
      maxEnd = Math.max(maxEnd, clipEnd);
    });
    // Instrument tracks end time
    instrumentTracks.forEach((t) => {
      if (!t.notes.length) return;
      const trackEnd = (t.startTime ?? 0) +
        Math.max(...t.notes.map((n) => (n.start + n.duration) * beatDur));
      maxEnd = Math.max(maxEnd, trackEnd);
    });
    // Convert to bars and add 4 bars of padding
    const contentBars = Math.ceil(maxEnd / barDuration) + 4;
    return Math.max(MIN_BARS, contentBars);
  }, [drumsStartTime, numBars, barDuration, audioTracks, instrumentTracks, bpm]);

  // Refs for scheduler (avoids stale closures)
  const patternRef = useRef(pattern);
  const bpmRef = useRef(bpm);
  const swingRef = useRef(swing);
  const mutedRowsRef = useRef(mutedRows);
  const metronomeOnRef = useRef(metronomeOn);
  const numBarsRef = useRef(numBars);
  const drumsMutedRef = useRef(drumsMuted);
  const audioTracksRef = useRef(audioTracks);
  const instrumentTracksRef = useRef(instrumentTracks);

  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { kitRef.current = kit; }, [kit]);
  useEffect(() => { swingRef.current = swing; }, [swing]);
  useEffect(() => { mutedRowsRef.current = mutedRows; }, [mutedRows]);
  useEffect(() => { metronomeOnRef.current = metronomeOn; }, [metronomeOn]);
  useEffect(() => { numBarsRef.current = numBars; }, [numBars]);
  useEffect(() => { drumsMutedRef.current = drumsMuted; }, [drumsMuted]);
  useEffect(() => { audioTracksRef.current = audioTracks; }, [audioTracks]);
  useEffect(() => { instrumentTracksRef.current = instrumentTracks; }, [instrumentTracks]);

  const totalSteps = numBars * STEPS_PER_BAR;

  // --- Session awareness ---
  const sessionRole = sessionRoleProp || null;
  const isSessionHost = sessionRole === "host";
  const isSessionViewer = sessionRole === "viewer";
  const isSessionCollab = sessionRole === "collaborator";
  const isReadOnly = isSessionViewer;
  const canDelete = !sessionRole || isSessionHost; // standalone or host
  const canEditTransport = !sessionRole || isSessionHost;
  const canEditSequencer = !sessionRole || isSessionHost || isSessionCollab;
  const canUploadRecord = !sessionRole || isSessionHost || isSessionCollab;

  const session = useDAWSession();
  const mediaStreamDestRef = useRef(null);

  // Get DAW audio as a MediaStream for Agora publishing
  const getAudioStream = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx || !masterGainRef.current) return null;
    if (mediaStreamDestRef.current) return mediaStreamDestRef.current.stream;
    const dest = ctx.createMediaStreamDestination();
    masterGainRef.current.connect(dest);
    mediaStreamDestRef.current = dest;
    return dest.stream;
  }, []);

  // Build a lightweight state snapshot for broadcasting
  const getStateSnapshot = useCallback(() => ({
    bpm,
    swing,
    kitId: kit.id,
    pattern,
    mutedRows,
    drumsMuted,
    drumsStartTime,
    padNames: customPadNames,
    customPadFlags,
    isPlaying,
    currentStep,
    metronomeOn,
    volume,
    numBars,
    audioTracks: audioTracks.map((t) => ({
      id: t.id, name: t.name, muted: t.muted, solo: t.solo,
      volume: t.volume, startTime: t.startTime, trimStart: t.trimStart, trimEnd: t.trimEnd,
    })),
    instrumentTracks: instrumentTracks.map((t) => ({
      id: t.id, name: t.name, muted: t.muted, solo: t.solo,
      volume: t.volume, startTime: t.startTime, instrumentType: t.instrumentType,
      notes: t.notes,
    })),
  }), [bpm, swing, kit, pattern, mutedRows, drumsMuted, drumsStartTime, customPadNames,
    customPadFlags, isPlaying, currentStep, metronomeOn, volume, numBars, audioTracks, instrumentTracks]);

  // Register state getter with session provider (host only)
  useEffect(() => {
    if (session?.registerStateGetter && isSessionHost) {
      session.registerStateGetter(getStateSnapshot);
    }
  }, [session, isSessionHost, getStateSnapshot]);

  // Apply remote state from host (viewer/collaborator)
  const applyRemoteState = useCallback((state) => {
    if (!state) return;
    setBpm(state.bpm);
    setSwing(state.swing);
    setMetronomeOn(state.metronomeOn);
    setVolume(state.volume);
    setNumBars(state.numBars);
    setDrumsMuted(state.drumsMuted);
    setDrumsStartTime(state.drumsStartTime);
    setMutedRows(state.mutedRows);
    setCustomPadNames(state.padNames);
    setCustomPadFlags(state.customPadFlags);
    setIsPlaying(state.isPlaying);
    setCurrentStep(state.currentStep);

    // Update pattern
    setPattern(state.pattern);
    patternRef.current = state.pattern;

    // Switch kit if different
    if (state.kitId && state.kitId !== kitRef.current.id) {
      const newKit = getKitById(state.kitId);
      if (newKit) setKit(newKit);
    }

    // Sync audio track metadata (without buffers — those come separately)
    setAudioTracks((prev) => {
      const updated = state.audioTracks.map((remote) => {
        const existing = prev.find((t) => t.id === remote.id);
        return existing
          ? { ...existing, ...remote }
          : { ...remote, buffer: null, automation: [], automationVisible: false };
      });
      return updated;
    });

    // Sync instrument track metadata
    setInstrumentTracks((prev) => {
      const updated = state.instrumentTracks.map((remote) => {
        const existing = prev.find((t) => t.id === remote.id);
        return existing
          ? { ...existing, ...remote }
          : { ...remote, customSampleBuffer: null, recordArmed: false, automation: [], automationVisible: false };
      });
      return updated;
    });
  }, []);

  // Listen for remote state updates (viewer/collaborator)
  useEffect(() => {
    if (!session?.remoteState || isSessionHost) return;
    applyRemoteState(session.remoteState);
  }, [session?.remoteState, isSessionHost, applyRemoteState]);

  // Resize pattern when numBars changes
  useEffect(() => {
    setPattern((prev) => {
      const newTotal = numBars * STEPS_PER_BAR;
      return prev.map((row) => {
        if (row.length === newTotal) return row;
        if (row.length < newTotal) return [...row, ...new Array(newTotal - row.length).fill(false)];
        return row.slice(0, newTotal);
      });
    });
  }, [numBars]);

  // Pad buffer management
  const replacePadBuffer = useCallback((padIndex, audioBuffer) => {
    sampleBuffersRef.current[padIndex] = audioBuffer;
  }, []);

  const resetPadBuffer = useCallback((padIndex) => {
    sampleBuffersRef.current[padIndex] = originalBuffersRef.current[padIndex];
  }, []);

  const setPadName = useCallback((padIndex, name) => {
    setCustomPadNames((prev) => {
      const next = [...prev];
      next[padIndex] = name || null;
      return next;
    });
  }, []);

  const resetPad = useCallback((padIndex) => {
    sampleBuffersRef.current[padIndex] = originalBuffersRef.current[padIndex];
    setCustomPadNames((prev) => {
      const next = [...prev];
      next[padIndex] = null;
      return next;
    });
    setCustomPadFlags((prev) => {
      const next = [...prev];
      next[padIndex] = false;
      return next;
    });
  }, []);

  const initAudio = useCallback(async () => {
    if (audioCtxRef.current) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;

    const masterGain = ctx.createGain();
    masterGain.gain.value = volume / 100;
    masterGain.connect(ctx.destination);
    masterGainRef.current = masterGain;

    const buffers = await generateKitBuffers(ctx, kitRef.current);
    sampleBuffersRef.current = buffers;
    originalBuffersRef.current = [...buffers];
    setIsReady(true);
  }, [volume]);

  const ensureAudioContext = useCallback(async () => {
    if (!audioCtxRef.current) await initAudio();
    if (audioCtxRef.current.state === "suspended") await audioCtxRef.current.resume();
  }, [initAudio]);

  // Register collab action handler (host receives collaborator uploads/actions)
  // Placed after ensureAudioContext to avoid temporal dead zone
  useEffect(() => {
    if (!session?.registerCollabActionHandler || !isSessionHost) return;
    session.registerCollabActionHandler(async (action) => {
      if (action.type === "toggle-step" && action.padIndex != null && action.stepIndex != null) {
        toggleStep(action.padIndex, action.stepIndex);
      } else if (action.type === "upload-pad" && action.padIndex != null && action.audioData) {
        try {
          await ensureAudioContext();
          const ctx = audioCtxRef.current;
          const decoded = await ctx.decodeAudioData(action.audioData.slice(0));
          sampleBuffersRef.current[action.padIndex] = decoded;
          setCustomPadNames((prev) => { const next = [...prev]; next[action.padIndex] = action.name || `Collab ${action.padIndex + 1}`; return next; });
          setCustomPadFlags((prev) => { const next = [...prev]; next[action.padIndex] = true; return next; });
          session.broadcastPadSample?.(action.padIndex, action.audioData, action.name);
        } catch (e) { console.warn("Failed to decode collab pad sample:", e); }
      } else if (action.type === "record-audio" && action.audioData) {
        try {
          await ensureAudioContext();
          const ctx = audioCtxRef.current;
          const decoded = await ctx.decodeAudioData(action.audioData.slice(0));
          addAudioTrack(action.name || `Collab Recording`, decoded, 0);
          session.broadcastAudioTrack?.(decoded.id, action.audioData, action.name);
        } catch (e) { console.warn("Failed to decode collab recording:", e); }
      }
    });
  }, [session, isSessionHost, ensureAudioContext]);

  const switchKit = useCallback(async (kitId) => {
    const newKit = getKitById(kitId);
    if (!newKit || newKit.id === kitRef.current.id) return;
    await ensureAudioContext();
    const ctx = audioCtxRef.current;
    const newBuffers = await generateKitBuffers(ctx, newKit);
    originalBuffersRef.current = [...newBuffers];
    // Preserve custom pads — only replace non-custom slots
    const merged = newBuffers.map((buf, i) =>
      customPadFlags[i] ? sampleBuffersRef.current[i] : buf
    );
    sampleBuffersRef.current = merged;
    setKit(newKit);
  }, [ensureAudioContext, customPadFlags]);

  const importPadSound = useCallback(async (padIndex, file) => {
    await ensureAudioContext();
    const ctx = audioCtxRef.current;
    const arrayBuffer = await file.arrayBuffer();
    const decoded = await ctx.decodeAudioData(arrayBuffer);
    sampleBuffersRef.current[padIndex] = decoded;
    const name = file.name.replace(/\.[^.]+$/, "");
    setCustomPadNames((prev) => {
      const next = [...prev];
      next[padIndex] = name;
      return next;
    });
    setCustomPadFlags((prev) => {
      const next = [...prev];
      next[padIndex] = true;
      return next;
    });
  }, [ensureAudioContext]);

  const playSampleAt = useCallback((padIndex, when) => {
    const ctx = audioCtxRef.current;
    const buffer = sampleBuffersRef.current[padIndex];
    if (!ctx || !buffer) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(masterGainRef.current);
    source.start(when);
  }, []);

  const triggerPad = useCallback(async (padIndex) => {
    await ensureAudioContext();
    playSampleAt(padIndex, 0);
    setActivePads((prev) => ({ ...prev, [padIndex]: true }));
    setTimeout(() => setActivePads((prev) => ({ ...prev, [padIndex]: false })), 150);
  }, [ensureAudioContext, playSampleAt]);

  // --- Step Sequencer ---

  const scheduleStep = useCallback((stepIndex, time) => {
    if (drumsMutedRef.current) return;
    const pat = patternRef.current;
    const muted = mutedRowsRef.current;

    for (let row = 0; row < NUM_PADS; row++) {
      if (muted[row]) continue;
      if (pat[row] && pat[row][stepIndex]) {
        playSampleAt(row, time);
      }
    }

    if (metronomeOnRef.current && stepIndex % 4 === 0) {
      const ctx = audioCtxRef.current;
      if (ctx) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = stepIndex % 16 === 0 ? 1200 : 900;
        gain.gain.setValueAtTime(0.2, time);
        gain.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
        osc.connect(gain).connect(masterGainRef.current);
        osc.start(time);
        osc.stop(time + 0.04);
      }
    }
  }, [playSampleAt]);

  // Schedule audio tracks to play when sequencer starts
  const scheduleAudioTracks = useCallback((loopStartTime) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    // Stop any currently playing audio track sources
    audioTrackSourcesRef.current.forEach((s) => { try { s.stop(); } catch(e) {} });
    audioTrackSourcesRef.current = [];

    const tracks = audioTracksRef.current;
    const hasSolo = tracks.some((t) => t.solo);

    tracks.forEach((track) => {
      if (track.muted) return;
      if (hasSolo && !track.solo) return;
      if (!track.buffer) return;

      const trimStart = track.trimStart ?? 0;
      const trimEnd = track.trimEnd ?? 1;
      const clipStartTime = track.startTime ?? 0;
      const bufferOffset = trimStart * track.buffer.duration;
      const clipDuration = (trimEnd - trimStart) * track.buffer.duration;

      if (clipDuration <= 0) return;

      const source = ctx.createBufferSource();
      source.buffer = track.buffer;
      const gainNode = ctx.createGain();
      source.connect(gainNode).connect(masterGainRef.current);

      // Schedule clip at its timeline position within this loop iteration
      const when = loopStartTime + clipStartTime;

      if (track.automation?.length > 0) {
        applyAutomation(gainNode, track.automation, loopStartTime, bpmRef.current);
      } else {
        gainNode.gain.value = (track.volume ?? 100) / 100;
      }

      source.start(when, bufferOffset, clipDuration);
      audioTrackSourcesRef.current.push(source);
    });
  }, []);

  // Schedule instrument tracks (all notes at once)
  const scheduleInstrumentTracks = useCallback((playbackStartTime) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    instrumentSourcesRef.current.forEach((s) => {
      try { if (s.stop) s.stop(); else if (s.carrier) { s.carrier.stop(); s.mod.stop(); } } catch (e) {}
    });
    instrumentSourcesRef.current = [];

    const tracks = instrumentTracksRef.current;
    const allTracks = [...audioTracksRef.current, ...tracks];
    const hasSolo = allTracks.some((t) => t.solo);
    const currentBpm = bpmRef.current;
    const beatDuration = 60 / currentBpm;

    tracks.forEach((track) => {
      if (track.muted) return;
      if (hasSolo && !track.solo) return;

      const trackOffset = track.startTime ?? 0;
      const vol = (track.volume ?? 100) / 100;
      const sfBuffers = !track.customSampleBuffer
        ? getCachedInstrument(track.instrumentType || "acoustic_grand_piano")
        : null;

      // Shared gain node per track for volume automation
      const trackGain = ctx.createGain();
      trackGain.connect(masterGainRef.current);
      if (track.automation?.length > 0) {
        applyAutomation(trackGain, track.automation, playbackStartTime, currentBpm);
      } else {
        trackGain.gain.value = vol;
      }

      track.notes.forEach((note) => {
        const noteStartSec = trackOffset + note.start * beatDuration;
        const noteDurationSec = note.duration * beatDuration;
        const when = playbackStartTime + noteStartSec;

        const gainNode = ctx.createGain();
        gainNode.gain.value = 1;
        gainNode.connect(trackGain);

        if (track.customSampleBuffer) {
          const source = playSampleNote(ctx, gainNode, track.customSampleBuffer, note.midi, when, noteDurationSec, note.velocity);
          instrumentSourcesRef.current.push(source);
        } else if (sfBuffers) {
          const source = playSoundfontNote(ctx, gainNode, sfBuffers, note.midi, when, noteDurationSec, note.velocity);
          instrumentSourcesRef.current.push(source);
        } else {
          const nodes = playFMNote(ctx, gainNode, note.midi, when, noteDurationSec, note.velocity);
          instrumentSourcesRef.current.push(nodes);
        }
      });
    });
  }, []);

  const startSequencer = useCallback(async () => {
    await ensureAudioContext();
    const ctx = audioCtxRef.current;

    currentStepRef.current = 0;
    nextStepTimeRef.current = ctx.currentTime;
    playbackStartTimeRef.current = ctx.currentTime;
    setCurrentStep(0);
    setPlayheadPosition(0);
    setIsPlaying(true);

    // Playhead animation — advances linearly (no wrapping)
    const animatePlayhead = () => {
      const elapsed = ctx.currentTime - playbackStartTimeRef.current;
      setPlayheadPosition(elapsed);
      animFrameRef.current = requestAnimationFrame(animatePlayhead);
    };
    animFrameRef.current = requestAnimationFrame(animatePlayhead);

    // Schedule audio and instrument tracks at their absolute positions
    scheduleAudioTracks(ctx.currentTime);
    scheduleInstrumentTracks(ctx.currentTime);

    const scheduler = () => {
      const currentBpm = bpmRef.current;
      const currentSwing = swingRef.current;
      const currentNumBars = numBarsRef.current;
      const currentTotalSteps = currentNumBars * STEPS_PER_BAR;
      const baseStepDuration = (60 / currentBpm) / 4;

      while (nextStepTimeRef.current < ctx.currentTime + SCHEDULE_AHEAD) {
        const step = currentStepRef.current;
        let stepTime = nextStepTimeRef.current;
        if (step % 2 === 1 && currentSwing > 0) {
          stepTime += baseStepDuration * (currentSwing / 200);
        }

        scheduleStep(step, stepTime);

        nextStepTimeRef.current += baseStepDuration;
        currentStepRef.current = (step + 1) % currentTotalSteps;

        const visualStep = currentStepRef.current;
        setTimeout(() => setCurrentStep(visualStep), 0);
      }
    };

    scheduler();
    schedulerRef.current = setInterval(scheduler, LOOKAHEAD_MS);
  }, [ensureAudioContext, scheduleStep, scheduleAudioTracks]);

  const stopSequencer = useCallback(() => {
    if (schedulerRef.current) {
      clearInterval(schedulerRef.current);
      schedulerRef.current = null;
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    audioTrackSourcesRef.current.forEach((s) => { try { s.stop(); } catch(e) {} });
    audioTrackSourcesRef.current = [];
    instrumentSourcesRef.current.forEach((s) => {
      try { if (s.stop) s.stop(); else if (s.carrier) { s.carrier.stop(); s.mod.stop(); } } catch (e) {}
    });
    instrumentSourcesRef.current = [];
    setIsPlaying(false);
    setCurrentStep(-1);
    setPlayheadPosition(0);
  }, []);

  const togglePlay = useCallback(async () => {
    if (isPlaying) stopSequencer();
    else await startSequencer();
  }, [isPlaying, startSequencer, stopSequencer]);

  // --- Pattern editing (immediate ref update for live editing) ---

  const toggleStep = useCallback((padIndex, stepIndex) => {
    setPattern((prev) => {
      const next = prev.map((row) => [...row]);
      next[padIndex][stepIndex] = !next[padIndex][stepIndex];
      // Immediately update ref so scheduler sees it
      patternRef.current = next;
      return next;
    });
  }, []);

  const clearPattern = useCallback(() => {
    const empty = createEmptyPattern(NUM_PADS, numBars * STEPS_PER_BAR);
    patternRef.current = empty;
    setPattern(empty);
  }, [numBars]);

  const toggleRowMute = useCallback((padIndex) => {
    setMutedRows((prev) => {
      const next = [...prev];
      next[padIndex] = !next[padIndex];
      mutedRowsRef.current = next;
      return next;
    });
  }, []);

  // --- Audio Track management ---

  const addAudioTrack = useCallback((name, audioBuffer, startTime = 0) => {
    audioTrackIdCounter += 1;
    const newTrack = {
      id: audioTrackIdCounter,
      name: name || `Audio ${audioTrackIdCounter}`,
      buffer: audioBuffer,
      muted: false,
      solo: false,
      volume: 100,
      startTime,
      trimStart: 0,
      trimEnd: 1,
      automation: [],
      automationVisible: false,
    };
    setAudioTracks((prev) => [...prev, newTrack]);
  }, []);

  const removeAudioTrack = useCallback((trackId) => {
    setAudioTracks((prev) => prev.filter((t) => t.id !== trackId));
  }, []);

  const toggleAudioTrackMute = useCallback((trackId) => {
    setAudioTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, muted: !t.muted } : t))
    );
  }, []);

  const toggleAudioTrackSolo = useCallback((trackId) => {
    setAudioTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, solo: !t.solo } : t))
    );
  }, []);

  const setAudioTrackVolume = useCallback((trackId, vol) => {
    setAudioTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, volume: vol } : t))
    );
  }, []);

  const setAudioTrackPosition = useCallback((trackId, startTime) => {
    setAudioTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, startTime: Math.max(0, startTime) } : t))
    );
  }, []);

  const setAudioTrackTrim = useCallback((trackId, trimStart, trimEnd) => {
    setAudioTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, trimStart, trimEnd } : t))
    );
  }, []);

  const importAudioFile = useCallback(async (file, startTime = 0) => {
    await ensureAudioContext();
    const ctx = audioCtxRef.current;
    const arrayBuffer = await file.arrayBuffer();
    const decoded = await ctx.decodeAudioData(arrayBuffer);
    const name = file.name.replace(/\.[^.]+$/, "");
    addAudioTrack(name, decoded, startTime);
  }, [ensureAudioContext, addAudioTrack]);

  const duplicateAudioTrack = useCallback((trackId) => {
    setAudioTracks((prev) => {
      const source = prev.find((t) => t.id === trackId);
      if (!source) return prev;
      audioTrackIdCounter += 1;
      const copy = {
        ...source,
        id: audioTrackIdCounter,
        name: source.name + " (copy)",
        automation: source.automation ? source.automation.map((p) => ({ ...p })) : [],
      };
      return [...prev, copy];
    });
  }, []);

  // --- Instrument Track management ---

  const addInstrumentTrack = useCallback(async (name) => {
    instrumentTrackIdCounter += 1;
    const newTrack = {
      id: instrumentTrackIdCounter,
      name: name || `Piano ${instrumentTrackIdCounter}`,
      notes: [],
      muted: false,
      solo: false,
      volume: 100,
      startTime: 0,
      customSampleBuffer: null,
      instrumentType: "acoustic_grand_piano",
      recordArmed: false,
      automation: [],
      automationVisible: false,
    };
    setInstrumentTracks((prev) => [...prev, newTrack]);
    setSelectedTrackId(newTrack.id);
    // Preload default instrument samples
    await ensureAudioContext();
    loadInstrument(audioCtxRef.current, "acoustic_grand_piano").catch(() => {});
  }, [ensureAudioContext]);

  const removeInstrumentTrack = useCallback((trackId) => {
    setInstrumentTracks((prev) => prev.filter((t) => t.id !== trackId));
  }, []);

  const updateInstrumentNotes = useCallback((trackId, notes) => {
    setInstrumentTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, notes } : t))
    );
  }, []);

  const toggleInstrumentTrackMute = useCallback((trackId) => {
    setInstrumentTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, muted: !t.muted } : t))
    );
  }, []);

  const toggleInstrumentTrackSolo = useCallback((trackId) => {
    setInstrumentTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, solo: !t.solo } : t))
    );
  }, []);

  const setInstrumentTrackVolume = useCallback((trackId, vol) => {
    setInstrumentTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, volume: vol } : t))
    );
  }, []);

  const setInstrumentTrackPosition = useCallback((trackId, startTime) => {
    setInstrumentTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, startTime: Math.max(0, startTime) } : t))
    );
  }, []);

  const duplicateInstrumentTrack = useCallback((trackId) => {
    setInstrumentTracks((prev) => {
      const source = prev.find((t) => t.id === trackId);
      if (!source) return prev;
      instrumentTrackIdCounter += 1;
      // Deep-copy notes with new IDs
      const notesCopy = source.notes.map((n) => ({ ...n, id: ++noteIdCounter }));
      const copy = {
        ...source,
        id: instrumentTrackIdCounter,
        name: source.name + " (copy)",
        notes: notesCopy,
        recordArmed: false,
        automation: source.automation ? source.automation.map((p) => ({ ...p })) : [],
      };
      return [...prev, copy];
    });
  }, []);

  const toggleInstrumentTrackRecordArm = useCallback((trackId) => {
    setInstrumentTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, recordArmed: !t.recordArmed } : t))
    );
  }, []);

  const setInstrumentSample = useCallback((trackId, audioBuffer) => {
    setInstrumentTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, customSampleBuffer: audioBuffer } : t))
    );
  }, []);

  const setInstrumentType = useCallback(async (trackId, instrumentType) => {
    setInstrumentTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, instrumentType, customSampleBuffer: null } : t))
    );
    // Preload samples for the new instrument
    await ensureAudioContext();
    loadInstrument(audioCtxRef.current, instrumentType).catch(() => {});
  }, [ensureAudioContext]);

  // --- Automation ---

  const setTrackAutomation = useCallback((trackId, points) => {
    const sorted = [...points].sort((a, b) => a.beat - b.beat);
    setAudioTracks((prev) => {
      const found = prev.find((t) => t.id === trackId);
      if (found) return prev.map((t) => (t.id === trackId ? { ...t, automation: sorted } : t));
      return prev;
    });
    setInstrumentTracks((prev) => {
      const found = prev.find((t) => t.id === trackId);
      if (found) return prev.map((t) => (t.id === trackId ? { ...t, automation: sorted } : t));
      return prev;
    });
  }, []);

  const toggleTrackAutomationVisible = useCallback((trackId) => {
    setAudioTracks((prev) => {
      const found = prev.find((t) => t.id === trackId);
      if (found) return prev.map((t) => (t.id === trackId ? { ...t, automationVisible: !t.automationVisible } : t));
      return prev;
    });
    setInstrumentTracks((prev) => {
      const found = prev.find((t) => t.id === trackId);
      if (found) return prev.map((t) => (t.id === trackId ? { ...t, automationVisible: !t.automationVisible } : t));
      return prev;
    });
  }, []);

  const triggerInstrumentNote = useCallback(async (midi) => {
    await ensureAudioContext();
    const ctx = audioCtxRef.current;
    const now = ctx.currentTime;
    const track = instrumentTracksRef.current.find((t) => t.id === selectedTrackId);
    const vol = track ? (track.volume ?? 100) / 100 : 1;
    const gainNode = ctx.createGain();
    gainNode.gain.value = vol;
    gainNode.connect(masterGainRef.current);
    if (track?.customSampleBuffer) {
      playSampleNote(ctx, gainNode, track.customSampleBuffer, midi, now, 0.5, 100);
    } else {
      // Try soundfont first, fall back to FM synthesis
      const sfBuffers = getCachedInstrument(track?.instrumentType || "acoustic_grand_piano");
      if (sfBuffers) {
        playSoundfontNote(ctx, gainNode, sfBuffers, midi, now, 0.5, 100);
      } else {
        playFMNote(ctx, gainNode, midi, now, 0.3, 100);
      }
    }
  }, [ensureAudioContext, selectedTrackId]);

  // --- MIDI Recording (per-track) ---

  const recordMidiNoteOn = useCallback((midi) => {
    if (!isPlaying) return;
    const ctx = audioCtxRef.current;
    if (!ctx) return;

    // Find all armed instrument tracks
    const armedTracks = instrumentTracksRef.current.filter((t) => t.recordArmed);
    if (armedTracks.length === 0) return;

    const elapsed = ctx.currentTime - playbackStartTimeRef.current;
    const currentBpm = bpmRef.current;
    const beatDuration = 60 / currentBpm;

    const entries = [];

    setInstrumentTracks((prev) =>
      prev.map((t) => {
        if (!t.recordArmed) return t;

        const trackOffset = t.startTime ?? 0;
        const startBeat = Math.max(0, (elapsed - trackOffset) / beatDuration);

        const id = ++noteIdCounter;
        entries.push({ id, startBeat, trackId: t.id });

        // Add note with placeholder duration — noteOff will finalize
        const newNote = { id, midi, start: startBeat, duration: 1, velocity: 100 };
        return { ...t, notes: [...t.notes, newNote] };
      })
    );

    // Track pending notes per midi key (multiple tracks possible)
    recordingNotesRef.current[midi] = entries;
  }, [isPlaying]);

  const recordMidiNoteOff = useCallback((midi) => {
    const entries = recordingNotesRef.current[midi];
    if (!entries || entries.length === 0) return;
    delete recordingNotesRef.current[midi];

    const ctx = audioCtxRef.current;
    if (!ctx) return;

    const elapsed = ctx.currentTime - playbackStartTimeRef.current;
    const currentBpm = bpmRef.current;
    const beatDuration = 60 / currentBpm;

    setInstrumentTracks((prev) =>
      prev.map((t) => {
        const entry = entries.find((e) => e.trackId === t.id);
        if (!entry) return t;

        const trackOffset = t.startTime ?? 0;
        const endBeat = Math.max(0, (elapsed - trackOffset) / beatDuration);
        const rawDuration = endBeat - entry.startBeat;
        // Use raw duration (no snapping) — minimum 0.25 beats (16th note)
        const finalDuration = Math.max(0.25, rawDuration);

        return {
          ...t,
          notes: t.notes.map((n) =>
            n.id === entry.id ? { ...n, duration: finalDuration } : n
          ),
        };
      })
    );
  }, []);

  // --- Save / Load Project ---

  const saveProject = useCallback(async () => {
    const ctx = audioCtxRef.current;
    const sampleRate = ctx?.sampleRate || 44100;

    // Encode audio track buffers as base64 WAV
    const encodedTracks = audioTracks.map((track) => {
      let wavBase64 = null;
      if (track.buffer) {
        const wavBlob = exportBufferToWav(track.buffer);
        // We need to do this synchronously — convert blob to base64
        // Use a workaround: write the buffer data directly
        wavBase64 = audioBufferToBase64(track.buffer);
      }
      return {
        id: track.id,
        name: track.name,
        muted: track.muted,
        solo: track.solo,
        volume: track.volume,
        startTime: track.startTime ?? 0,
        trimStart: track.trimStart ?? 0,
        trimEnd: track.trimEnd ?? 1,
        automation: track.automation || [],
        automationVisible: track.automationVisible || false,
        wavBase64,
        sampleRate,
      };
    });

    // Encode custom pad sounds
    const customPads = customPadFlags.map((isCustom, i) => {
      if (!isCustom) return null;
      const buffer = sampleBuffersRef.current[i];
      if (!buffer) return null;
      return {
        name: customPadNames[i],
        wavBase64: audioBufferToBase64(buffer),
      };
    });

    // Encode instrument tracks
    const encodedInstrumentTracks = instrumentTracks.map((track) => ({
      id: track.id,
      name: track.name,
      notes: track.notes,
      muted: track.muted,
      solo: track.solo,
      volume: track.volume,
      startTime: track.startTime ?? 0,
      instrumentType: track.instrumentType || "acoustic_grand_piano",
      automation: track.automation || [],
      automationVisible: track.automationVisible || false,
      customSampleBase64: track.customSampleBuffer
        ? audioBufferToBase64(track.customSampleBuffer)
        : null,
    }));

    const project = {
      version: 6,
      kitId: kit.id,
      bpm,
      swing,
      numBars,
      pattern,
      mutedRows,
      drumsMuted,
      drumsStartTime,
      customPadNames,
      customPads,
      audioTracks: encodedTracks,
      instrumentTracks: encodedInstrumentTracks,
    };

    const json = JSON.stringify(project);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `project-${Date.now()}.daw.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [bpm, swing, numBars, pattern, mutedRows, drumsMuted, drumsStartTime, customPadNames, customPadFlags, audioTracks, instrumentTracks]);

  const loadProject = useCallback(async (file) => {
    await ensureAudioContext();
    const ctx = audioCtxRef.current;

    const text = await file.text();
    const project = JSON.parse(text);

    if (isPlaying) stopSequencer();

    setBpm(project.bpm || 120);
    setSwing(project.swing || 0);
    setNumBars(project.numBars || 1);
    setDrumsMuted(project.drumsMuted || false);
    setDrumsStartTime(project.drumsStartTime || 0);

    // Restore kit (v6+, defaults to classic-808 for older projects)
    const targetKit = project.kitId ? getKitById(project.kitId) : DEFAULT_KIT;
    setKit(targetKit);
    const kitBuffers = await generateKitBuffers(ctx, targetKit);
    sampleBuffersRef.current = [...kitBuffers];
    originalBuffersRef.current = [...kitBuffers];

    // Restore pattern
    if (project.pattern) {
      const newPattern = project.pattern;
      patternRef.current = newPattern;
      setPattern(newPattern);
    }

    if (project.mutedRows) {
      mutedRowsRef.current = project.mutedRows;
      setMutedRows(project.mutedRows);
    }

    // Restore custom pad names and sounds
    if (project.customPadNames) {
      setCustomPadNames(project.customPadNames);
    } else {
      setCustomPadNames(new Array(NUM_PADS).fill(null));
    }
    if (project.customPads) {
      const flags = new Array(NUM_PADS).fill(false);
      project.customPads.forEach((pad, i) => {
        if (pad && pad.wavBase64) {
          const buffer = base64ToAudioBuffer(ctx, pad.wavBase64, ctx.sampleRate);
          if (buffer) {
            sampleBuffersRef.current[i] = buffer;
            flags[i] = true;
          }
        }
      });
      setCustomPadFlags(flags);
    } else {
      setCustomPadFlags(new Array(NUM_PADS).fill(false));
      // Reset all pads to originals
      for (let i = 0; i < NUM_PADS; i++) {
        if (originalBuffersRef.current[i]) {
          sampleBuffersRef.current[i] = originalBuffersRef.current[i];
        }
      }
    }

    // Restore audio tracks
    if (project.audioTracks) {
      const restored = [];
      for (const t of project.audioTracks) {
        let buffer = null;
        if (t.wavBase64) {
          buffer = base64ToAudioBuffer(ctx, t.wavBase64, t.sampleRate || ctx.sampleRate);
        }
        audioTrackIdCounter = Math.max(audioTrackIdCounter, t.id);
        restored.push({
          id: t.id,
          name: t.name,
          buffer,
          muted: t.muted || false,
          solo: t.solo || false,
          volume: t.volume ?? 100,
          startTime: t.startTime ?? 0,
          trimStart: t.trimStart ?? 0,
          trimEnd: t.trimEnd ?? 1,
          automation: t.automation || [],
          automationVisible: t.automationVisible || false,
        });
      }
      setAudioTracks(restored);
    }

    // Restore instrument tracks (version 4+)
    if (project.instrumentTracks) {
      const restoredInst = project.instrumentTracks.map((t) => {
        let customSampleBuffer = null;
        if (t.customSampleBase64) {
          customSampleBuffer = base64ToAudioBuffer(ctx, t.customSampleBase64, ctx.sampleRate);
        }
        instrumentTrackIdCounter = Math.max(instrumentTrackIdCounter, t.id);
        // Ensure all note IDs are tracked
        t.notes.forEach((n) => { noteIdCounter = Math.max(noteIdCounter, n.id || 0); });
        const instrumentType = t.instrumentType || "acoustic_grand_piano";
        // Preload soundfont for this instrument
        if (!customSampleBuffer) {
          loadInstrument(ctx, instrumentType).catch(() => {});
        }
        return {
          id: t.id,
          name: t.name,
          notes: t.notes || [],
          muted: t.muted || false,
          solo: t.solo || false,
          volume: t.volume ?? 100,
          startTime: t.startTime ?? 0,
          instrumentType,
          customSampleBuffer,
          recordArmed: false,
          automation: t.automation || [],
          automationVisible: t.automationVisible || false,
        };
      });
      setInstrumentTracks(restoredInst);
    } else {
      setInstrumentTracks([]);
    }
  }, [ensureAudioContext, isPlaying, stopSequencer]);

  // --- Export WAV (drums + audio tracks mixed) ---

  const exportSession = useCallback(async () => {
    const stepsTotal = numBars * STEPS_PER_BAR;
    const stepDuration = (60 / bpm) / 4;
    const drumDuration = stepsTotal * stepDuration;
    const beatDuration = 60 / bpm;

    // Solo check across all track types
    const hasSolo = [...audioTracks, ...instrumentTracks].some((t) => t.solo);

    // Find total duration including all track types
    let maxDuration = drumsStartTime + drumDuration;
    audioTracks.forEach((t) => {
      if (t.muted || (hasSolo && !t.solo) || !t.buffer) return;
      const clipEnd = (t.startTime ?? 0) + ((t.trimEnd ?? 1) - (t.trimStart ?? 0)) * t.buffer.duration;
      maxDuration = Math.max(maxDuration, clipEnd);
    });
    instrumentTracks.forEach((t) => {
      if (t.muted || (hasSolo && !t.solo) || !t.notes.length) return;
      const trackEnd = (t.startTime ?? 0) +
        Math.max(...t.notes.map((n) => (n.start + n.duration) * beatDuration));
      maxDuration = Math.max(maxDuration, trackEnd);
    });

    const sampleRate = audioCtxRef.current?.sampleRate || 44100;
    const offline = new OfflineAudioContext(2, Math.ceil(sampleRate * Math.max(maxDuration, 0.1)), sampleRate);

    // Schedule drums
    if (!drumsMuted) {
      for (let row = 0; row < NUM_PADS; row++) {
        if (mutedRows[row]) continue;
        for (let step = 0; step < stepsTotal; step++) {
          if (!pattern[row][step]) continue;
          const buffer = sampleBuffersRef.current[row];
          if (!buffer) continue;
          const source = offline.createBufferSource();
          source.buffer = buffer;
          source.connect(offline.destination);
          source.start(drumsStartTime + step * stepDuration);
        }
      }
    }

    // Schedule audio tracks
    audioTracks.forEach((track) => {
      if (track.muted || (hasSolo && !track.solo) || !track.buffer) return;
      const trimStart = track.trimStart ?? 0;
      const trimEnd = track.trimEnd ?? 1;
      const bufferOffset = trimStart * track.buffer.duration;
      const clipDuration = (trimEnd - trimStart) * track.buffer.duration;
      if (clipDuration <= 0) return;
      const source = offline.createBufferSource();
      source.buffer = track.buffer;
      const gainNode = offline.createGain();
      if (track.automation?.length > 0) {
        applyAutomation(gainNode, track.automation, 0, bpm);
      } else {
        gainNode.gain.value = (track.volume ?? 100) / 100;
      }
      source.connect(gainNode).connect(offline.destination);
      source.start(track.startTime ?? 0, bufferOffset, clipDuration);
    });

    // Schedule instrument tracks
    instrumentTracks.forEach((track) => {
      if (track.muted || (hasSolo && !track.solo)) return;
      const trackOffset = track.startTime ?? 0;
      const vol = (track.volume ?? 100) / 100;
      const sfBuffers = !track.customSampleBuffer
        ? getCachedInstrument(track.instrumentType || "acoustic_grand_piano")
        : null;

      // Shared gain node per track for volume automation
      const trackGain = offline.createGain();
      trackGain.connect(offline.destination);
      if (track.automation?.length > 0) {
        applyAutomation(trackGain, track.automation, 0, bpm);
      } else {
        trackGain.gain.value = vol;
      }

      track.notes.forEach((note) => {
        const noteStart = trackOffset + note.start * beatDuration;
        const noteDur = note.duration * beatDuration;
        const gainNode = offline.createGain();
        gainNode.gain.value = 1;
        gainNode.connect(trackGain);
        if (track.customSampleBuffer) {
          playSampleNote(offline, gainNode, track.customSampleBuffer, note.midi, noteStart, noteDur, note.velocity);
        } else if (sfBuffers) {
          playSoundfontNote(offline, gainNode, sfBuffers, note.midi, noteStart, noteDur, note.velocity);
        } else {
          playFMNote(offline, gainNode, note.midi, noteStart, noteDur, note.velocity);
        }
      });
    });

    return offline.startRendering();
  }, [pattern, mutedRows, drumsMuted, drumsStartTime, numBars, bpm, audioTracks, instrumentTracks]);

  // --- Volume ---

  const changeVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    if (masterGainRef.current) masterGainRef.current.gain.value = newVolume / 100;
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (schedulerRef.current) clearInterval(schedulerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      audioTrackSourcesRef.current.forEach((s) => { try { s.stop(); } catch(e) {} });
      instrumentSourcesRef.current.forEach((s) => {
        try { if (s.stop) s.stop(); else if (s.carrier) { s.carrier.stop(); s.mod.stop(); } } catch (e) {}
      });
    };
  }, []);

  const value = {
    isReady, kit, switchKit, bpm, setBpm, swing, setSwing, volume, changeVolume,
    metronomeOn, setMetronomeOn, triggerPad, activePads, initAudio, ensureAudioContext,
    // Sequencer
    pattern, toggleStep, clearPattern, currentStep, isPlaying, togglePlay,
    startSequencer, stopSequencer, numBars, setNumBars, totalSteps,
    mutedRows, toggleRowMute, drumsMuted, setDrumsMuted,
    // Audio tracks
    audioTracks, addAudioTrack, removeAudioTrack, duplicateAudioTrack,
    toggleAudioTrackMute, toggleAudioTrackSolo, setAudioTrackVolume,
    setAudioTrackPosition, setAudioTrackTrim, importAudioFile,
    drumsStartTime, setDrumsStartTime,
    // Pad buffer & custom sounds
    replacePadBuffer, resetPadBuffer, customPadNames, customPadFlags,
    setPadName, importPadSound, resetPad,
    // Instrument tracks
    instrumentTracks, addInstrumentTrack, removeInstrumentTrack, duplicateInstrumentTrack,
    updateInstrumentNotes, toggleInstrumentTrackMute, toggleInstrumentTrackSolo,
    setInstrumentTrackVolume, setInstrumentTrackPosition, setInstrumentSample,
    setInstrumentType, triggerInstrumentNote, noteIdCounter: () => ++noteIdCounter,
    toggleInstrumentTrackRecordArm, recordMidiNoteOn, recordMidiNoteOff,
    // Automation
    setTrackAutomation, toggleTrackAutomationVisible,
    // Arrangement
    selectedTrackId, setSelectedTrackId, playheadPosition, arrangementBars, barDuration,
    // Project
    saveProject, loadProject, exportSession, audioCtxRef,
    // Session (collaborative)
    sessionRole, isReadOnly, canDelete, canEditTransport, canEditSequencer, canUploadRecord,
    getAudioStream, getStateSnapshot,
  };

  return <DAWContext.Provider value={value}>{children}</DAWContext.Provider>;
}

// --- Helpers for project save/load ---

function audioBufferToBase64(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const sampleRate = audioBuffer.sampleRate;

  // Interleave float32 channel data
  const interleaved = new Float32Array(length * numChannels);
  for (let ch = 0; ch < numChannels; ch++) {
    const channelData = audioBuffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      interleaved[i * numChannels + ch] = channelData[i];
    }
  }

  // Convert to base64
  const bytes = new Uint8Array(interleaved.buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary) + "|" + numChannels + "|" + length + "|" + sampleRate;
}

function base64ToAudioBuffer(ctx, encoded, fallbackSampleRate) {
  const parts = encoded.split("|");
  const base64 = parts[0];
  const numChannels = parseInt(parts[1]) || 1;
  const length = parseInt(parts[2]) || 0;
  const sampleRate = parseInt(parts[3]) || fallbackSampleRate || ctx.sampleRate;

  if (!length) return null;

  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  const interleaved = new Float32Array(bytes.buffer);
  const buffer = ctx.createBuffer(numChannels, length, sampleRate);

  for (let ch = 0; ch < numChannels; ch++) {
    const channelData = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      channelData[i] = interleaved[i * numChannels + ch];
    }
  }

  return buffer;
}
